import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import './skeleton.css'

/** Match the corners of what is loading: a line of text, a field, a pill button, 16:9 media, a panel or an avatar. */
export type SkeletonShape = 'text' | 'field' | 'button' | 'media' | 'panel' | 'circle'

export type SkeletonProps = HTMLAttributes<HTMLDivElement> & {
  shape?: SkeletonShape
}

/** A placeholder block in the shape of pending content. Size it with classes; it is hidden from assistive technology. */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { shape = 'text', className, ...props },
  ref,
) {
  return <div ref={ref} aria-hidden="true" className={cn('play-skeleton', `play-skeleton--${shape}`, className)} {...props} />
})

export type SkeletonGroupProps = Omit<HTMLAttributes<HTMLDivElement>, 'role'> & {
  /** What is loading, read once by assistive technology, for example “Loading cameras”. */
  label: string
  children: ReactNode
}

/** Wraps the skeletons for one region and announces it as loading. */
export const SkeletonGroup = forwardRef<HTMLDivElement, SkeletonGroupProps>(function SkeletonGroup(
  { label, className, children, ...props },
  ref,
) {
  return (
    <div ref={ref} role="status" aria-label={label} aria-busy="true" className={cn('play-skeleton-group', className)} {...props}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  )
})
